import { cn } from '@/lib/cn';

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  id?: string;
  disabled?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  className?: string;
}

/**
 * On/off switch for binary settings (UAT-3: switches, not "Turn on/Turn off" text buttons).
 * A native <button> with role="switch": Space and Enter activate it through the browser's own
 * button behaviour, so no key handler is needed. No Radix dependency (none installed).
 * Callers must give it a name (aria-label or aria-labelledby).
 */
export function Switch({
  checked,
  onCheckedChange,
  id,
  disabled = false,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  className,
}: SwitchProps): JSX.Element {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      data-state={checked ? 'checked' : 'unchecked'}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none',
        checked ? 'border-primary bg-primary' : 'border-border bg-muted',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none block h-3.5 w-3.5 rounded-full shadow-sm transition-transform motion-reduce:transition-none',
          checked ? 'translate-x-[18px] bg-primary-foreground' : 'translate-x-0.5 bg-muted-foreground',
        )}
      />
    </button>
  );
}
